/**
 * BETA — Nomenclature (MSPRH base nationale) handlers.
 *
 * Powers the ProduitForm autofill + POS autocomplete in demo mode. The real
 * backend hits a server-side nomenclature service backed by the imported CNAS
 * catalogue; the demo has no such DB, so we serve a small hard-coded list of
 * well-known Algerian-market products.
 *
 * The catalogue is also exported so chifa.js can drive /chifa/lookup off the
 * same source of truth (a found row maps to a remboursable line with
 * taux/libelle, an unknown num returns { found: false }).
 *
 * Row shape — matches server/src/services/nomenclature.service.js so the page
 * code (PortalCatalogue.jsx) doesn't care whether it's hitting the real API or
 * the mock:
 *   { id, num_enregistrement, nom_marque, dci, forme, dosage, laboratoire,
 *     conditionnement, classe_therapeutique, remboursable, taux_remboursement,
 *     libelle }
 */

// Hard-coded catalogue of well-known Algerian-market products. Numéros
// d'enregistrement use the MSPRH "AA/BB/CCCCC" pattern (two-digit class /
// two-digit subclass / five-digit sequence) — purely cosmetic for the demo.
export const NOMENCLATURE_CATALOGUE = [
  {
    id: "nom-001",
    num_enregistrement: "52/A1/00289",
    nom_marque: "Doliprane 1000mg",
    dci: "Paracétamol",
    forme: "Comprimé",
    dosage: "1000 mg",
    laboratoire: "Sanofi",
    conditionnement: "Boîte de 8 comprimés",
    classe_therapeutique: "Antalgique / Antipyrétique",
    remboursable: true,
    taux_remboursement: 80,
    libelle: "Paracétamol 1g — antalgique de palier 1",
  },
  {
    id: "nom-002",
    num_enregistrement: "52/A1/00290",
    nom_marque: "Doliprane 500mg",
    dci: "Paracétamol",
    forme: "Comprimé",
    dosage: "500 mg",
    laboratoire: "Sanofi",
    conditionnement: "Boîte de 16 comprimés",
    classe_therapeutique: "Antalgique / Antipyrétique",
    remboursable: true,
    taux_remboursement: 80,
    libelle: "Paracétamol 500mg — antalgique de palier 1",
  },
  {
    id: "nom-003",
    num_enregistrement: "54/B2/00143",
    nom_marque: "Augmentin 1g",
    dci: "Amoxicilline + Acide clavulanique",
    forme: "Comprimé pelliculé",
    dosage: "1 g",
    laboratoire: "GSK",
    conditionnement: "Boîte de 14 comprimés",
    classe_therapeutique: "Antibiotique — Bêta-lactamine",
    remboursable: true,
    taux_remboursement: 80,
    libelle: "Amoxicilline-clavulanate 1g — antibiothérapie large spectre",
  },
  {
    id: "nom-004",
    num_enregistrement: "54/B2/00144",
    nom_marque: "Augmentin Sirop",
    dci: "Amoxicilline + Acide clavulanique",
    forme: "Sirop",
    dosage: "100/12,5 mg/mL",
    laboratoire: "GSK",
    conditionnement: "Flacon 60 mL",
    classe_therapeutique: "Antibiotique — Bêta-lactamine",
    remboursable: true,
    taux_remboursement: 80,
    libelle: "Amoxicilline-clavulanate suspension pédiatrique",
  },
  {
    id: "nom-005",
    num_enregistrement: "54/B1/00121",
    nom_marque: "Amoxil 500mg",
    dci: "Amoxicilline",
    forme: "Gélule",
    dosage: "500 mg",
    laboratoire: "GSK",
    conditionnement: "Boîte de 12 gélules",
    classe_therapeutique: "Antibiotique — Bêta-lactamine",
    remboursable: true,
    taux_remboursement: 80,
    libelle: "Amoxicilline 500mg — antibiothérapie",
  },
  {
    id: "nom-006",
    num_enregistrement: "57/E3/00341",
    nom_marque: "Coversyl 5mg",
    dci: "Périndopril arginine",
    forme: "Comprimé pelliculé",
    dosage: "5 mg",
    laboratoire: "Servier",
    conditionnement: "Boîte de 30 comprimés",
    classe_therapeutique: "Antihypertenseur — IEC",
    remboursable: true,
    taux_remboursement: 80,
    libelle: "Périndopril 5mg — HTA / insuffisance cardiaque",
  },
  {
    id: "nom-007",
    num_enregistrement: "61/H1/00207",
    nom_marque: "Lévothyrox 100µg",
    dci: "Lévothyroxine sodique",
    forme: "Comprimé sécable",
    dosage: "100 µg",
    laboratoire: "Merck",
    conditionnement: "Boîte de 30 comprimés",
    classe_therapeutique: "Hormone thyroïdienne",
    remboursable: true,
    taux_remboursement: 100,
    libelle: "Lévothyroxine 100µg — hypothyroïdie",
  },
  {
    id: "nom-008",
    num_enregistrement: "60/G2/00188",
    nom_marque: "Glucophage 500mg",
    dci: "Metformine chlorhydrate",
    forme: "Comprimé pelliculé",
    dosage: "500 mg",
    laboratoire: "Merck",
    conditionnement: "Boîte de 30 comprimés",
    classe_therapeutique: "Antidiabétique oral — Biguanide",
    remboursable: true,
    taux_remboursement: 80,
    libelle: "Metformine 500mg — diabète de type 2",
  },
  {
    id: "nom-009",
    num_enregistrement: "60/G1/00410",
    nom_marque: "Lantus 100UI/mL",
    dci: "Insuline glargine",
    forme: "Solution injectable (stylo)",
    dosage: "100 UI/mL",
    laboratoire: "Sanofi",
    conditionnement: "Stylo prérempli 3 mL × 5",
    classe_therapeutique: "Insuline lente",
    remboursable: true,
    taux_remboursement: 100,
    libelle: "Insuline glargine — diabète insulino-traité",
  },
  {
    id: "nom-010",
    num_enregistrement: "60/G3/00422",
    nom_marque: "Janumet 50/1000",
    dci: "Sitagliptine + Metformine",
    forme: "Comprimé pelliculé",
    dosage: "50 mg / 1000 mg",
    laboratoire: "Merck",
    conditionnement: "Boîte de 56 comprimés",
    classe_therapeutique: "Antidiabétique — Association",
    remboursable: true,
    taux_remboursement: 80,
    libelle: "Sitagliptine-metformine — diabète de type 2",
  },
  {
    id: "nom-011",
    num_enregistrement: "60/G1/00415",
    nom_marque: "Insulatard",
    dci: "Insuline humaine isophane (NPH)",
    forme: "Solution injectable (flacon)",
    dosage: "100 UI/mL",
    laboratoire: "Novartis",
    conditionnement: "Flacon 10 mL",
    classe_therapeutique: "Insuline intermédiaire",
    remboursable: true,
    taux_remboursement: 100,
    libelle: "Insuline NPH — diabète insulino-traité",
  },
  {
    id: "nom-012",
    num_enregistrement: "57/F1/00505",
    nom_marque: "Crestor 10mg",
    dci: "Rosuvastatine calcique",
    forme: "Comprimé pelliculé",
    dosage: "10 mg",
    laboratoire: "Pfizer",
    conditionnement: "Boîte de 30 comprimés",
    classe_therapeutique: "Hypolipémiant — Statine",
    remboursable: true,
    taux_remboursement: 80,
    libelle: "Rosuvastatine 10mg — dyslipidémie",
  },
  {
    id: "nom-013",
    num_enregistrement: "57/F1/00501",
    nom_marque: "Tahor 20mg",
    dci: "Atorvastatine calcique",
    forme: "Comprimé pelliculé",
    dosage: "20 mg",
    laboratoire: "Pfizer",
    conditionnement: "Boîte de 30 comprimés",
    classe_therapeutique: "Hypolipémiant — Statine",
    remboursable: true,
    taux_remboursement: 80,
    libelle: "Atorvastatine 20mg — dyslipidémie",
  },
  {
    id: "nom-014",
    num_enregistrement: "53/C2/00718",
    nom_marque: "Spasfon",
    dci: "Phloroglucinol + Triméthylphloroglucinol",
    forme: "Comprimé enrobé",
    dosage: "80 mg",
    laboratoire: "Merinal",
    conditionnement: "Boîte de 30 comprimés",
    classe_therapeutique: "Antispasmodique",
    remboursable: false,
    taux_remboursement: 0,
    libelle: "Phloroglucinol — antispasmodique digestif / urinaire",
  },
  {
    id: "nom-015",
    num_enregistrement: "53/C3/00802",
    nom_marque: "Smecta",
    dci: "Diosmectite",
    forme: "Poudre pour suspension orale",
    dosage: "3 g/sachet",
    laboratoire: "El Kendi",
    conditionnement: "Boîte de 30 sachets",
    classe_therapeutique: "Antidiarrhéique d'origine minérale",
    remboursable: false,
    taux_remboursement: 0,
    libelle: "Diosmectite — diarrhée aiguë",
  },
  {
    id: "nom-016",
    num_enregistrement: "55/D1/00911",
    nom_marque: "Mucothiol 200mg",
    dci: "N-Acétylcystéine",
    forme: "Sachet (poudre orale)",
    dosage: "200 mg",
    laboratoire: "Saidal",
    conditionnement: "Boîte de 30 sachets",
    classe_therapeutique: "Mucolytique",
    remboursable: true,
    taux_remboursement: 80,
    libelle: "N-Acétylcystéine 200mg — encombrement bronchique",
  },
  {
    id: "nom-017",
    num_enregistrement: "65/M2/00134",
    nom_marque: "Bétadine 10%",
    dci: "Povidone iodée",
    forme: "Solution pour application cutanée",
    dosage: "10 %",
    laboratoire: "Bayer",
    conditionnement: "Flacon 125 mL",
    classe_therapeutique: "Antiseptique — Iodé",
    remboursable: false,
    taux_remboursement: 0,
    libelle: "Povidone iodée 10% — antiseptique cutané",
  },
  {
    id: "nom-018",
    num_enregistrement: "55/D2/00610",
    nom_marque: "Ventoline Inhalateur",
    dci: "Salbutamol sulfate",
    forme: "Aérosol-doseur (inhalateur)",
    dosage: "100 µg/dose",
    laboratoire: "GSK",
    conditionnement: "Flacon pressurisé 200 doses",
    classe_therapeutique: "Bronchodilatateur — β2 mimétique courte durée",
    remboursable: true,
    taux_remboursement: 80,
    libelle: "Salbutamol inhalé — crise d'asthme / BPCO",
  },
  {
    id: "nom-019",
    num_enregistrement: "58/N1/00208",
    nom_marque: "Lexomil 6mg",
    dci: "Bromazépam",
    forme: "Comprimé quadrisécable",
    dosage: "6 mg",
    laboratoire: "Roche",
    conditionnement: "Boîte de 30 comprimés",
    classe_therapeutique: "Anxiolytique — Benzodiazépine",
    remboursable: false,
    taux_remboursement: 0,
    libelle: "Bromazépam 6mg — anxiété (Tableau B, ordonnance sécurisée)",
  },
  {
    id: "nom-020",
    num_enregistrement: "55/D2/00721",
    nom_marque: "Toplexil",
    dci: "Oxomémazine + Guaïfénésine",
    forme: "Sirop",
    dosage: "0,33 mg + 33,3 mg / mL",
    laboratoire: "Sanofi",
    conditionnement: "Flacon 150 mL",
    classe_therapeutique: "Antitussif d'action centrale",
    remboursable: false,
    taux_remboursement: 0,
    libelle: "Oxomémazine — toux sèche non productive",
  },
];

/** Lookup by exact num_enregistrement. Returns the row or null. */
export function findByNumEnregistrement(num) {
  if (!num) return null;
  const needle = String(num).trim();
  return (
    NOMENCLATURE_CATALOGUE.find((r) => r.num_enregistrement === needle) || null
  );
}

export function register(route) {
  route("GET", "/nomenclature/search", (ctx) => {
    const q = String(ctx.params.q ?? "").trim().toLowerCase();
    const limit = Math.min(Number(ctx.params.limit) || 15, 50);
    if (!q) return [];
    const rows = NOMENCLATURE_CATALOGUE.filter((r) => {
      return (
        r.nom_marque.toLowerCase().includes(q) ||
        r.num_enregistrement.toLowerCase().includes(q) ||
        r.dci.toLowerCase().includes(q) ||
        r.laboratoire.toLowerCase().includes(q)
      );
    });
    return rows.slice(0, limit);
  });
}
